import { z } from 'zod';
import { LogicalModelSchema } from './ai';

const SlotModels = z.object({
  HEAVY: z.string(),
  ROUTINE: z.string(),
  CHEAP: z.string(),
  INTAKE: z.string(),
});

export const AiSettingsDtoSchema = z.object({
  organizationId: z.string().guid(),
  modelHeavy: z.string().nullable(),
  modelRoutine: z.string().nullable(),
  modelCheap: z.string().nullable(),
  modelIntake: z.string().nullable(),
  /** The env default per slot (what "Alapértelmezett" means); may be a `latest:` reference. */
  defaults: SlotModels,
  /** What each slot is set to — an override or the env default; may be a `latest:` reference. */
  configured: SlotModels,
  /** The concrete model each slot runs right now (`latest:` references resolved). */
  effective: SlotModels,
  provider: z.enum(['openrouter', 'dryrun']),
  baseUrl: z.string().url().nullable(),
  aiEnabled: z.boolean(),
  updatedAt: z.string().nullable(),
});
export type AiSettingsDto = z.infer<typeof AiSettingsDtoSchema>;

/**
 * A model setting: a concrete OpenRouter slug (`anthropic/claude-sonnet-5.5`)
 * or `latest:<line>` (`latest:anthropic/claude-sonnet`), which always runs the
 * newest model of that line.
 */
const ModelRefSchema = z
  .string()
  .min(1)
  .max(120)
  .refine((v) => !v.startsWith('latest:') || /^latest:[a-z0-9-]+\/[a-z0-9.-]+$/.test(v), {
    message: 'invalid latest: reference',
  });

export const UpdateAiSettingsSchema = z.object({
  modelHeavy: ModelRefSchema.nullable().optional(),
  modelRoutine: ModelRefSchema.nullable().optional(),
  modelCheap: ModelRefSchema.nullable().optional(),
  modelIntake: ModelRefSchema.nullable().optional(),
});
export type UpdateAiSettings = z.infer<typeof UpdateAiSettingsSchema>;

export type ModelFamily = 'anthropic' | 'openai' | 'google' | 'other';

/** One concrete model in the picker. */
export interface CatalogModel {
  id: string;
  label: string;
  family: ModelFamily;
  /** Release date (ISO), null for the static fallback list. */
  createdAt: string | null;
}

/** A "Mindig a legújabb …" choice and the model it runs today. */
export interface LatestModelOption {
  id: string;
  label: string;
  family: ModelFamily;
  resolvesTo: string | null;
}

export interface ModelCatalogDto {
  /** `live`: from OpenRouter; `fallback`: the static list below (catalog unreachable). */
  source: 'live' | 'fallback';
  latest: LatestModelOption[];
  models: CatalogModel[];
}

/**
 * Static fallback for the picker when the live OpenRouter catalog is
 * unreachable. IDs are exact OpenRouter slugs, checked 2026-10-28. Normally
 * the live list replaces it, so it only needs to be good enough to save a
 * setting during an outage.
 */
export const MODEL_CATALOG: CatalogModel[] = [
  {
    id: 'anthropic/claude-opus-5.5',
    label: 'Claude Opus 5.5',
    family: 'anthropic',
    createdAt: null,
  },
  {
    id: 'anthropic/claude-sonnet-5.5',
    label: 'Claude Sonnet 5.5',
    family: 'anthropic',
    createdAt: null,
  },
  {
    id: 'anthropic/claude-haiku-5.5',
    label: 'Claude Haiku 5.5',
    family: 'anthropic',
    createdAt: null,
  },
  {
    id: 'anthropic/claude-fable-5.1',
    label: 'Claude Fable 5.1',
    family: 'anthropic',
    createdAt: null,
  },
  { id: 'openai/gpt-6.1-sol', label: 'GPT-6.1 Sol', family: 'openai', createdAt: null },
  { id: 'google/gemini-3.8-flash', label: 'Gemini 3.8 Flash', family: 'google', createdAt: null },
];

export { LogicalModelSchema };
